import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PatientSearchDropdown } from "./PatientSearchDropdown";
import { apiService } from "@/lib/api";
import type { HealthMetric, TrackerEntryForm } from "@/types/tracker";
import type { User } from "@/types";
import { SiteIcon } from "@/components/icons/siteIcons";
import { getSiteIcon } from "@/components/icons/siteIconRegistry";
import {
  iconography,
  trackerMetricIconography,
  trackerMetricOrder,
  type TrackerMetricId,
} from "@/design-system/tokens/iconography";
import { Loader2 } from "lucide-react";
import { TrackerDetailPanel } from "./trackers/TrackerDetailPanel";
import { AddTrackerEntryModal } from "./AddTrackerEntryModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const healthMetricSnapshots: Record<TrackerMetricId, string> = {
  "heart-rate": "90-180",
  "blood-pressure": "170-150/78-80",
  "blood-glucose": "120-290",
  temperature: "98.5-102",
  "respiratory-rate": "73-80",
  hip: "80-83.5",
  "waist-size": "98.5 - 100",
  weight: "90-92.5",
};

const healthMetrics: HealthMetric[] = trackerMetricOrder.map((metricId) => {
  const metric = trackerMetricIconography[metricId];

  return {
    id: metricId,
    title: metric.label,
    value: healthMetricSnapshots[metricId],
    unit: metric.unit,
    period: "Last 7 days",
    category: metric.category,
  };
});

export const TrackerDashboard = () => {
  const [selectedMetric, setSelectedMetric] = useState<HealthMetric | null>(
    null
  );
  const [selectedPatient, setSelectedPatient] = useState<User | null>(null);
  const [trackerValues, setTrackerValues] = useState<Record<string, string>>(
    {}
  );
  const [isLoadingTrackerData, setIsLoadingTrackerData] = useState(false);

  // Add Entry Modal State
  const [isAddEntryModalOpen, setIsAddEntryModalOpen] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success"
  );

  // Delete Confirmation State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [readingToDelete, setReadingToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);

  // Function to process tracker data and extract min/max values
  const processTrackerData = (results: any[]) => {
    const values: Record<string, string> = {};

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        const { type, data } = result.value;

        switch (type) {
          case "blood-glucose": {
            if (data && data.length > 0) {
              const readingValues = data.map((reading: any) => reading.value);
              const min = Math.min(...readingValues);
              const max = Math.max(...readingValues);
              values[type] = `${min}-${max}`;
            } else {
              values[type] = "N/A";
            }
            break;
          }

          case "blood-pressure": {
            if (data && data.systolic && data.diastolic) {
              const systolicValues =
                data.systolic.data?.map((reading: any) => reading.value) || [];
              const diastolicValues =
                data.diastolic.data?.map((reading: any) => reading.value) || [];

              if (systolicValues.length > 0 && diastolicValues.length > 0) {
                const systolicMin = Math.min(...systolicValues);
                const systolicMax = Math.max(...systolicValues);
                const diastolicMin = Math.min(...diastolicValues);
                const diastolicMax = Math.max(...diastolicValues);
                values[type] =
                  `${systolicMin}-${systolicMax}/${diastolicMin}-${diastolicMax}`;
              } else {
                values[type] = "N/A";
              }
            } else {
              values[type] = "N/A";
            }
            break;
          }

          case "heart-rate":
          case "waist-size":
          case "hip":
          case "weight":
          case "respiratory-rate":
          case "temperature": {
            if (data && data.length > 0) {
              const readingValues = data.map((reading: any) => reading.value);
              const min = Math.min(...readingValues);
              const max = Math.max(...readingValues);
              values[type] = `${min}-${max}`;
            } else {
              values[type] = "N/A";
            }
            break;
          }
        }
      } else {
        const type = result.reason?.config?.url?.includes("blood-glucose")
          ? "blood-glucose"
          : result.reason?.config?.url?.includes("blood-pressure")
            ? "blood-pressure"
            : result.reason?.config?.url?.includes("heart-beat")
              ? "heart-rate"
              : result.reason?.config?.url?.includes("waist-size")
                ? "waist-size"
                : result.reason?.config?.url?.includes("hip-size")
                  ? "hip"
                  : result.reason?.config?.url?.includes("weight")
                    ? "weight"
                    : result.reason?.config?.url?.includes("respiratory-rate")
                      ? "respiratory-rate"
                      : result.reason?.config?.url?.includes("temperature")
                        ? "temperature"
                        : "unknown";
        values[type] = "N/A";
      }
    });

    return values;
  };

  // Fetch tracker data when patient is selected
  useEffect(() => {
    const fetchTrackerData = async () => {
      if (!selectedPatient?.id) {
        setTrackerValues({});
        return;
      }

      setIsLoadingTrackerData(true);
      try {
        const results = await apiService.getTrackerMinMaxValues(
          selectedPatient.id
        );
        const processedValues = processTrackerData(results);
        setTrackerValues(processedValues);
      } catch (error) {
        console.error("Error fetching tracker data:", error);
        setTrackerValues({});
      } finally {
        setIsLoadingTrackerData(false);
      }
    };

    fetchTrackerData();
  }, [selectedPatient?.id]);

  const [showPatientPrompt, setShowPatientPrompt] = useState(false);

  const handleCardClick = (metric: HealthMetric) => {
    if (!selectedPatient) {
      setShowPatientPrompt(true);
      setTimeout(() => setShowPatientPrompt(false), 3000);
      return;
    }
    setSelectedMetric(metric);
  };

  const handleCloseDetail = () => {
    setSelectedMetric(null);
  };

  const handleAddReading = () => {
    setIsAddEntryModalOpen(true);
  };

  const handleAddEntryClose = () => {
    setIsAddEntryModalOpen(false);
  };

  const handleAddEntrySubmit = (_data: TrackerEntryForm) => {
    // Entry submitted - the modal handles the API call
  };

  const handleDataSubmitted = () => {
    setSnackbarMessage("Reading added successfully");
    setSnackbarType("success");
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);

    // Refresh tracker values
    if (selectedPatient?.id) {
      apiService.getTrackerMinMaxValues(selectedPatient.id).then((results) => {
        const processedValues = processTrackerData(results);
        setTrackerValues(processedValues);
      });
    }
  };

  const handleDataSubmissionError = (
    errorMessage: string = "Failed to save data"
  ) => {
    setSnackbarMessage(errorMessage);
    setSnackbarType("error");
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  const handleDeleteClick = (readingId: string) => {
    setReadingToDelete(readingId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!readingToDelete || !selectedPatient?.id || !selectedMetric) return;

    setIsDeleting(true);
    try {
      switch (selectedMetric.id) {
        case "blood-glucose":
          await apiService.deleteBloodGlucoseReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "blood-pressure":
          await apiService.deleteBloodPressureReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "heart-rate":
          await apiService.deleteHeartRateReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "waist-size":
          await apiService.deleteWaistSizeReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "hip":
          await apiService.deleteHipSizeReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "weight":
          await apiService.deleteWeightReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "respiratory-rate":
          await apiService.deleteRespiratoryRateReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
        case "temperature":
          await apiService.deleteTemperatureReading(
            readingToDelete,
            selectedPatient.id
          );
          break;
      }

      setSnackbarMessage("Reading deleted successfully");
      setSnackbarType("success");
      setShowSnackbar(true);
      setTimeout(() => setShowSnackbar(false), 3000);

      // Refresh tracker values
      const results = await apiService.getTrackerMinMaxValues(
        selectedPatient.id
      );
      const processedValues = processTrackerData(results);
      setTrackerValues(processedValues);
    } catch (error: any) {
      if (error.status === 403) {
        setDeleteErrorOpen(true);
      } else {
        setSnackbarMessage(error.message || "Failed to delete reading");
        setSnackbarType("error");
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 3000);
      }
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setReadingToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setReadingToDelete(null);
  };

  // Get config for selected metric
  const selectedMetricConfig = selectedMetric
    ? trackerMetricIconography[selectedMetric.id as TrackerMetricId]
    : null;

  return (
    <div className="flex h-full overflow-hidden p-2 sm:p-3">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-border/50">
        {/* Page Header */}
        <div className="p-6 border-b border-border/50 bg-card shrink-0">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">
                Health Trackers
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Monitor patient health metrics over time
              </p>
            </div>
            <div className="w-full max-w-sm">
              <PatientSearchDropdown
                selectedPatient={selectedPatient}
                onPatientSelect={setSelectedPatient}
                placeholder="Search and select a patient..."
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Split View Content */}
        <div className="flex-1 flex gap-4 p-4 min-h-0 overflow-hidden">
          {/* LEFT PANEL - Trackers List */}
          <div className="w-[45%] min-w-[380px] max-w-[500px] flex flex-col">
            <Card className="h-full flex flex-col">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">
                  Trackers
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-auto pt-0">
                <div className="grid grid-cols-2 gap-3">
                  {healthMetrics.map((metric) => {
                    const config =
                      trackerMetricIconography[metric.id as TrackerMetricId];
                    const isSelected = selectedMetric?.id === metric.id;
                    const hasPatient = !!selectedPatient;

                    return (
                      <Card
                        key={metric.id}
                        className={cn(
                          "transition-all duration-200 border-2",
                          "bg-card",
                          config?.cardBorderClassName || "border-border",
                          hasPatient
                            ? "cursor-pointer hover:bg-accent/50 hover:shadow-md hover:-translate-y-0.5"
                            : "opacity-60 cursor-not-allowed",
                          isSelected && hasPatient
                            ? "ring-2 ring-primary ring-offset-2 border-primary"
                            : ""
                        )}
                        onClick={() => handleCardClick(metric)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className={cn(
                                "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                                config?.iconSurfaceClassName || "bg-muted"
                              )}
                            >
                              <SiteIcon
                                name={config?.iconKey || "activity"}
                                className={cn(
                                  iconography.size.inline,
                                  config?.iconColorClassName ||
                                    "text-foreground"
                                )}
                              />
                            </div>
                            <span className="text-sm font-medium text-foreground truncate">
                              {metric.title}
                              {!hasPatient && (
                                <span className="block text-[10px] text-muted-foreground font-normal">
                                  Select patient
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="space-y-0.5">
                            <div className="text-xl font-bold text-foreground">
                              {hasPatient ? (
                                isLoadingTrackerData ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  trackerValues[metric.id] || "--"
                                )
                              ) : (
                                "--"
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {metric.unit}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT PANEL - Tracker Detail */}
          <div className="flex-1 min-w-0">
            <TrackerDetailPanel
              metric={selectedMetric}
              metricIcon={getSiteIcon(
                selectedMetricConfig?.iconKey || "activity"
              )}
              metricColor={
                selectedMetricConfig?.iconColorValue ||
                "oklch(var(--foreground))"
              }
              selectedPatient={selectedPatient}
              onAddReading={selectedPatient ? handleAddReading : undefined}
              onDeleteReading={handleDeleteClick}
              onClose={selectedMetric ? handleCloseDetail : undefined}
            />
          </div>
        </div>
      </Card>

      {/* Add Entry Modal */}
      {selectedMetric && (
        <AddTrackerEntryModal
          isOpen={isAddEntryModalOpen}
          onClose={handleAddEntryClose}
          metric={selectedMetric}
          onSubmit={handleAddEntrySubmit}
          selectedPatient={selectedPatient}
          onDataSubmitted={handleDataSubmitted}
          onDataSubmissionError={handleDataSubmissionError}
          darkMode={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-foreground">
              Delete this record?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              This action cannot be undone. This will permanently delete the
              reading.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-end space-x-2">
            <AlertDialogCancel
              onClick={handleDeleteCancel}
              className="px-4 py-2 text-muted-foreground bg-white border border-border rounded-md hover:bg-muted"
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="px-4 py-2 text-white bg-destructive rounded-md hover:bg-destructive disabled:opacity-50"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Error Dialog */}
      <AlertDialog open={deleteErrorOpen} onOpenChange={setDeleteErrorOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-foreground">
              Deletion not available
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              This reading is locked for deletion to maintain accurate records.
              If you need assistance, please contact your care team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-end">
            <AlertDialogAction
              onClick={() => setDeleteErrorOpen(false)}
              className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90"
            >
              Okay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Patient Prompt Toast */}
      {showPatientPrompt && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-bottom-2">
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border bg-warning/10 border-warning/30 text-warning">
            <span className="text-sm font-medium">
              Please select a patient first to view tracker data
            </span>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {showSnackbar && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-bottom-2">
          <div
            className={cn(
              "flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border",
              snackbarType === "success"
                ? "bg-success border-success/30 text-success"
                : "bg-destructive border-destructive/30 text-destructive"
            )}
          >
            <span className="text-sm font-medium">{snackbarMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};
